import EventBus from 'eventing-bus';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Delete as DeleteIcon, Add as AddIcon, DragIndicator as DragIcon, Code as CodeIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { Table, TableBody, TableCell, TableHead, TableRow, TextField, FormControlLabel, Checkbox, IconButton, Box, Typography, Divider, Slider, Popover, Paper } from '@mui/material';

import { createPipeline, updatePipeline, deletePipeline, setPipelineSettingsModal } from '../../store/Deals';

const PipelineSettings = ({ isCreateMode = false, onClose: customOnClose }) => {

  const dispatch = useDispatch();
  const { pipelineBoard, pipelineSettingsModal, currentPipeline } = useSelector(state => state['Deals']);
  const isOpen = isCreateMode ? true : pipelineSettingsModal;

  const [errors, setErrors] = useState({});
  const [newStageName, setNewStageName] = useState('');
  const [hoveredStageId, setHoveredStageId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverStageId, setPopoverStageId] = useState(null);
  const [stageTooltipTimeout, setStageTooltipTimeout] = useState(null);
  const [pipelineAnchorEl, setPipelineAnchorEl] = useState(null);
  const [pipelineTooltipTimeout, setPipelineTooltipTimeout] = useState(null);
  const [recentlyMovedStageId, setRecentlyMovedStageId] = useState(null);
  const [isDeletePipelineModalOpen, setIsDeletePipelineModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    stages: [],
    description: '',
    settings: {
      dealRottenDays: 30,
      requireAmount: false,
      autoRottenDeals: true,
      allowSkipStages: true,
      requireCloseDate: false,
    }
  });

  useEffect(() => {
    if (isCreateMode) {
      // Set default data for create mode
      const defaultStages = [
        { id: 'lead', name: 'Lead', order: 0, probability: 10, color: '#64B5F6', rottenDays: 30, isWon: false, isLost: false },
        { id: 'contacted', name: 'Contacted', order: 1, probability: 20, color: '#4FC3F7', rottenDays: 30, isWon: false, isLost: false },
        { id: 'qualified', name: 'Qualified', order: 2, probability: 40, color: '#4DD0E1', rottenDays: 30, isWon: false, isLost: false },
        { id: 'proposal', name: 'Proposal', order: 3, probability: 60, color: '#4DB6AC', rottenDays: 30, isWon: false, isLost: false },
        { id: 'negotiation', name: 'Negotiation', order: 4, probability: 80, color: '#66BB6A', rottenDays: 30, isWon: false, isLost: false },
        { id: 'won', name: 'Won', order: 5, probability: 100, color: '#81C784', rottenDays: 30, isWon: true, isLost: false },
        { id: 'lost', name: 'Lost', order: 6, probability: 0, color: '#E57373', rottenDays: 30, isWon: false, isLost: true }
      ];
      setFormData({
        name: '',
        description: '',
        stages: defaultStages,
        settings: { autoRottenDeals: true, dealRottenDays: 30, allowSkipStages: true, requireCloseDate: false, requireAmount: false }
      });
    } else if (currentPipeline) {
      setFormData({
        name: currentPipeline['name'] || '',
        description: currentPipeline['description'] || '',
        stages: currentPipeline['stages'] || [],
        settings: currentPipeline['settings'] || { autoRottenDeals: true, dealRottenDays: 30, allowSkipStages: true, requireCloseDate: false, requireAmount: false }
      });
    }
  }, [currentPipeline, isCreateMode]);

  const handleClose = () => {
    if (customOnClose) customOnClose();
    else dispatch(setPipelineSettingsModal(false));

    setFormData({ name: '', description: '', stages: [], settings: { autoRottenDeals: true, dealRottenDays: 30, allowSkipStages: true, requireCloseDate: false, requireAmount: false } });
    setNewStageName('');
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSettingChange = (setting, value) => {
    setFormData(prev => ({ ...prev, settings: { ...prev['settings'], [setting]: value } }));
  };

  const handleStageChange = (stageId, field, value) => {
    setFormData(prev => ({ ...prev, stages: prev['stages'].map(stage => stage['id'] === stageId ? { ...stage, [field]: value } : stage) }));
  };

  const handleAddStage = () => {
    if (!newStageName.trim()) return EventBus.publish('error', 'Stage name is required');
    const newStage = { id: `stage-${Date.now()}`, name: newStageName.trim(), order: formData['stages'].length, probability: 0, color: '#1976d2', rottenDays: 30, isWon: false, isLost: false };

    setFormData(prev => ({ ...prev, stages: [...prev['stages'], newStage] }));
    setNewStageName('');
  };

  const handleRemoveStage = (stageId) => {
    // Only check for deals if editing an existing pipeline, not when creating a new one
    if (!isCreateMode) {
      // Find the stage in the pipeline board to check for deals
      let stage = pipelineBoard['stages']?.find(stage => stage['_id'] === stageId || stage['id'] === stageId || stage['stageId'] === stageId);
      
      if (stage && stage['deals']?.length > 0) {
        const dealCount = stage['deals'].length;
        const stageName = stage['name'] || stage['stageName'] || 'this stage';
        EventBus.publish('error', `Cannot remove "${stageName}" - it contains ${dealCount} deal${dealCount > 1 ? 's' : ''}. Please move or delete the deals first.`);
        return;
      }
    }

    // Check if this is the last Won or Lost stage
    const stageToRemove = formData['stages'].find(s => s['id'] === stageId);
    if (stageToRemove) {
      const remainingStages = formData['stages'].filter(s => s['id'] !== stageId);
      const remainingWonStages = remainingStages.filter(s => s['isWon']);
      const remainingLostStages = remainingStages.filter(s => s['isLost']);
      
      if (stageToRemove['isWon'] && remainingWonStages.length === 0) {
        EventBus.publish('error', 'Cannot remove the last "Won" stage. At least one Won stage is required.');
        return;
      }
      
      if (stageToRemove['isLost'] && remainingLostStages.length === 0) {
        EventBus.publish('error', 'Cannot remove the last "Lost" stage. At least one Lost stage is required.');
        return;
      }
      
      if (remainingStages.length === 0) {
        EventBus.publish('error', 'Cannot remove the last stage. At least one stage is required.');
        return;
      }
    }

    setFormData(prev => ({ ...prev, stages: prev['stages'].filter(stage => stage['id'] !== stageId).map((stage, index) => ({ ...stage, order: index })) }));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    // Check if the stage actually moved to a different position
    if (result.source.index === result.destination.index) return;

    const items = Array.from(formData['stages']);
    const [reorderedItem] = items.splice(result['source']['index'], 1);
    items.splice(result['destination']['index'], 0, reorderedItem);

    setFormData(prev => ({ ...prev, stages: items.map((stage, index) => ({ ...stage, order: index })) }));
    
    // Highlight the moved stage
    setRecentlyMovedStageId(reorderedItem.id);
    
    // Remove highlight after 1.5 seconds
    setTimeout(() => {
      setRecentlyMovedStageId(null);
    }, 1500);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData['name'].trim()) newErrors['name'] = 'Pipeline name is required';
    if (formData['stages'].length === 0) newErrors['stages'] = 'At least one stage is required';

    const hasWonStage = formData['stages'].some(stage => stage['isWon']);
    const hasLostStage = formData['stages'].some(stage => stage['isLost']);

    if (!hasWonStage) newErrors['wonStage'] = 'At least one "Won" stage is required';
    if (!hasLostStage) newErrors['lostStage'] = 'At least one "Lost" stage is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      // Build specific error message
      let errorMessages = [];
      if (errors['name']) errorMessages.push('Pipeline name is required');
      if (errors['stages']) errorMessages.push('At least one stage is required');
      if (errors['wonStage']) errorMessages.push('At least one "Won" stage is required');
      if (errors['lostStage']) errorMessages.push('At least one "Lost" stage is required');
      
      const errorMessage = errorMessages.length > 0 
        ? errorMessages.join('. ') 
        : 'Please fix the errors before saving';
      
      EventBus.publish('error', errorMessage);
      return;
    }

    if (isCreateMode) dispatch(createPipeline(formData));
    else if (currentPipeline?.['_id']) dispatch(updatePipeline({ id: currentPipeline['_id'], ...formData }));

    handleClose();
  }

  const handleDeletePipeline = (confirmed = false) => {
    const totalDeals = pipelineBoard?.['stages']?.reduce((total, stage) => total + (stage['deals']?.length || 0), 0) || 0;
    const pipelineName = formData['name'] || currentPipeline?.['name'];
    if (!confirmed) {
      if (totalDeals > 0) {
        EventBus.publish('error', `Cannot delete "${pipelineName}" - it contains ${totalDeals} deal${totalDeals > 1 ? 's' : ''}. Please move or delete the deals first.`);
        return;
      }
      setIsDeletePipelineModalOpen(true);
    } else if (currentPipeline?.['_id']) {
      dispatch(deletePipeline(currentPipeline['_id']));
      setIsDeletePipelineModalOpen(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} modalClassName='right right-without-bg' className={`main-modal right right-side-modal pipeline-modal deal-pipeline`}>
        <ModalHeader toggle={handleClose}><h3>{isCreateMode ? 'Create New Pipeline' : 'Pipeline Settings'}</h3></ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>
            <div className='createdeal-form'>
              <h4>Basic Information</h4>
              <div className='group-form'>
                <label className="label-with-icon">
                  Pipeline Name
                  {!isCreateMode && currentPipeline?.['_id'] && (
                    <>
                      <IconButton
                        size="small"
                        className="pipeline-id-icon-btn"
                        onMouseEnter={(e) => {
                          // Cancel any pending close timeout
                          if (pipelineTooltipTimeout) {
                            clearTimeout(pipelineTooltipTimeout);
                            setPipelineTooltipTimeout(null);
                          }
                          setPipelineAnchorEl(e.currentTarget);
                        }}
                        onMouseLeave={() => {
                          // Small delay to allow moving to tooltip
                          const timeout = setTimeout(() => {
                            setPipelineAnchorEl(null);
                          }, 150);
                          setPipelineTooltipTimeout(timeout);
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <CodeIcon fontSize="small" />
                      </IconButton>
                      <Popover
                        open={Boolean(pipelineAnchorEl)}
                        anchorEl={pipelineAnchorEl}
                        onClose={() => {
                          if (pipelineTooltipTimeout) {
                            clearTimeout(pipelineTooltipTimeout);
                            setPipelineTooltipTimeout(null);
                          }
                          setPipelineAnchorEl(null);
                        }}
                        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                        className="pipeline-id-popover"
                        disableRestoreFocus
                      >
                        <Paper 
                          elevation={0}
                          className="pipeline-id-popover-content"
                          onMouseEnter={(e) => {
                            e.stopPropagation();
                            // Cancel any pending close timeout when entering tooltip
                            if (pipelineTooltipTimeout) {
                              clearTimeout(pipelineTooltipTimeout);
                              setPipelineTooltipTimeout(null);
                            }
                          }}
                          onMouseLeave={() => {
                            if (pipelineTooltipTimeout) {
                              clearTimeout(pipelineTooltipTimeout);
                              setPipelineTooltipTimeout(null);
                            }
                            setPipelineAnchorEl(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography variant="subtitle2" className="pipeline-id-title">
                            Pipeline ID
                          </Typography>
                          <Typography variant="body2" className="pipeline-id-description">
                            This unique identifier will be used when syncing with integration partners. It can't be edited.
                          </Typography>
                          <Box className="pipeline-id-input-container">
                            <TextField
                              fullWidth
                              size="small"
                              value={currentPipeline['_id']}
                              InputProps={{ 
                                readOnly: true,
                                className: "pipeline-id-input"
                              }}
                              className="pipeline-id-textfield"
                            />
                            <IconButton
                              size="small"
                              className="pipeline-id-copy-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigator.clipboard.writeText(currentPipeline['_id']);
                                EventBus.publish('success', 'Pipeline ID copied to clipboard');
                                setPipelineAnchorEl(null);
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Popover>
                    </>
                  )}
                </label>
                <TextField
                  fullWidth
                  placeholder="Add pipeline name"
                  value={formData['name']}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors['name']}
                  helperText={errors['name']}
                  margin="normal"
                />
              </div>
              <div className='group-form'>
                <label>Description</label>
                <TextField
                  fullWidth
                  placeholder="Add description"
                  value={formData['description']}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={1}
                  margin="normal"
                />
              </div>
              <div className='group-form'>
                <Divider />
              </div>
              <h4>Pipeline Settings</h4>
              <div className='group-form'>
                <FormControlLabel
                  control={<Checkbox checked={formData['settings']['autoRottenDeals']} onChange={(e) => handleSettingChange('autoRottenDeals', e.target.checked)} />}
                  label="Automatically mark deals as rotten"
                />
                {formData['settings']['autoRottenDeals'] && (
                  <Box ml={4} mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Days until deal is marked as rotten: {formData['settings']['dealRottenDays']}
                    </Typography>
                    <Slider
                      value={formData['settings']['dealRottenDays']}
                      onChange={(e, value) => handleSettingChange('dealRottenDays', value)}
                      min={1}
                      max={90}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                )}
              </div>
              <div className='group-form checkbox-group'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData['settings']['allowSkipStages']}
                      onChange={(e) => handleSettingChange('allowSkipStages', e.target.checked)}
                    />
                  }
                  label="Allow skipping stages"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData['settings']['requireCloseDate']}
                      onChange={(e) => handleSettingChange('requireCloseDate', e.target.checked)}
                    />
                  }
                  label="Require close date"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData['settings']['requireAmount']}
                      onChange={(e) => handleSettingChange('requireAmount', e.target.checked)}
                    />
                  }
                  label="Require deal amount"
                />
              </div>
              <div className='group-form'>
                <Divider />
              </div>
              <h4>Pipeline Stages</h4>
              <div className='group-form'>
                {errors['stages'] && (
                  <Typography color="error" variant="body2" className="pipeline-error-message">
                    ⚠️ {errors['stages']}
                  </Typography>
                )}

                {errors['wonStage'] && (
                  <Typography color="error" variant="body2" className="pipeline-error-message">
                    ⚠️ {errors['wonStage']}
                  </Typography>
                )}
                
                {errors['lostStage'] && (
                  <Typography color="error" variant="body2" className="pipeline-error-message">
                    ⚠️ {errors['lostStage']}
                  </Typography>
                )}
                
                {Object.keys(errors).length > 0 && (
                  <Typography variant="caption" color="textSecondary" className="pipeline-error-note">
                    Note: A pipeline must have at least one "Won" stage and one "Lost" stage to properly track deal outcomes.
                  </Typography>
                )}
              </div>
              {/* <div className='group-form add-stage'>
                <TextField
                  size="small"
                  placeholder="New stage name"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e['target']['value'])}
                  onKeyPress={(e) => e['key'] === 'Enter' && handleAddStage()}
                  style={{ marginRight: 8 }}
                />
                <button className='btn-style-border' onClick={handleAddStage}><AddIcon /> Add Stage</button>
              </div> */}
              <div className='group-form'>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="stages">
                    {(provided) => (
                      <Table {...provided.droppableProps} ref={provided.innerRef}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Stage Name</TableCell>
                            <TableCell>DEALS</TableCell>
                            <TableCell>Probability %</TableCell>
                            <TableCell>Won</TableCell>
                            <TableCell>Lost</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {formData.stages.map((stage, index) => (
                            <Draggable key={stage.id} draggableId={stage.id} index={index}>
                              {(provided, snapshot) => {
                                // Merge react-beautiful-dnd's transform with rotation tilt
                                // Subtle tilt from top left corner when dragging (same as deal cards)
                                const style = snapshot.isDragging && provided.draggableProps.style?.transform
                                  ? {
                                      ...provided.draggableProps.style,
                                      transform: `${provided.draggableProps.style.transform} rotate(3deg)`,
                                      transformOrigin: 'left top', // Tilt from top left corner
                                      display: 'flex',
                                      width: '100%',
                                      left: 0,
                                    }
                                  : provided.draggableProps.style;

                                const isRecentlyMoved = recentlyMovedStageId === stage.id;
                                
                                return (
                                <TableRow
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`${snapshot.isDragging ? 'pipeline-stage-dragging' : ''} ${isRecentlyMoved ? 'pipeline-stage-recently-moved' : ''}`}
                                  style={style}
                                  onMouseEnter={() => {
                                    // Close tooltip from any other stage when entering this row
                                    if (popoverStageId && popoverStageId !== stage['id']) {
                                      setAnchorEl(null);
                                      setPopoverStageId(null);
                                    }
                                  }}
                                >

                                  <TableCell  {...provided.dragHandleProps}>
                                    <div className='drag-box'>
                                      <DragIcon color="action" />
                                      <Box 
                                        className="stage-name-container"
                                        onMouseEnter={() => {
                                          // Close any open tooltip from other stages immediately
                                          if (popoverStageId && popoverStageId !== stage.id) {
                                            setAnchorEl(null);
                                            setPopoverStageId(null);
                                          }
                                          setHoveredStageId(stage.id);
                                        }}
                                        onMouseLeave={() => {
                                          setHoveredStageId(null);
                                        }}
                                      >
                                        <div className="stage-name-with-icon">
                                          <TextField
                                            size="small"
                                            fullWidth
                                            value={stage.name}
                                            onChange={(e) =>
                                              handleStageChange(stage.id, "name", e.target.value)
                                            }
                                          />
                                          {!isCreateMode && (stage._id || stage.id) && (
                                            <>
                                              <IconButton
                                                size="small"
                                                className="stage-id-icon-btn"
                                                sx={{ 
                                                  opacity: (index === 0 || hoveredStageId === stage.id) ? 1 : 0,
                                                  transition: 'opacity 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                  // Cancel any pending close timeout
                                                  if (stageTooltipTimeout) {
                                                    clearTimeout(stageTooltipTimeout);
                                                    setStageTooltipTimeout(null);
                                                  }
                                                  // Close any other open tooltips
                                                  if (popoverStageId && popoverStageId !== stage.id) {
                                                    setAnchorEl(null);
                                                    setPopoverStageId(null);
                                                  }
                                                  setAnchorEl(e.currentTarget);
                                                  setPopoverStageId(stage.id);
                                                }}
                                                onMouseLeave={() => {
                                                  // Small delay to allow moving to tooltip
                                                  const timeout = setTimeout(() => {
                                                    setAnchorEl(null);
                                                    setPopoverStageId(null);
                                                  }, 150);
                                                  setStageTooltipTimeout(timeout);
                                                }}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                }}
                                              >
                                                <CodeIcon fontSize="small" />
                                              </IconButton>
                                              <Popover
                                                open={popoverStageId === stage.id && Boolean(anchorEl)}
                                                anchorEl={anchorEl}
                                                onClose={() => {
                                                  if (stageTooltipTimeout) {
                                                    clearTimeout(stageTooltipTimeout);
                                                    setStageTooltipTimeout(null);
                                                  }
                                                  setAnchorEl(null);
                                                  setPopoverStageId(null);
                                                }}
                                                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                                                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                                className="stage-id-popover"
                                                disableRestoreFocus
                                              >
                                                <Paper 
                                                  elevation={0}
                                                  className="stage-id-popover-content"
                                                  onMouseEnter={(e) => {
                                                    e.stopPropagation();
                                                    if (stageTooltipTimeout) {
                                                      clearTimeout(stageTooltipTimeout);
                                                      setStageTooltipTimeout(null);
                                                    }
                                                  }}
                                                  onMouseLeave={() => {
                                                    if (stageTooltipTimeout) {
                                                      clearTimeout(stageTooltipTimeout);
                                                      setStageTooltipTimeout(null);
                                                    }
                                                    setAnchorEl(null);
                                                    setPopoverStageId(null);
                                                  }}
                                                >
                                                  <Typography variant="subtitle2" className="stage-id-title">
                                                    Internal id
                                                  </Typography>
                                                  <Typography variant="body2" className="stage-id-description">
                                                    This unique identifier will be used when syncing with integration partners. It can't be edited.
                                                  </Typography>
                                                  <Box className="stage-id-input-container">
                                                    <TextField
                                                      fullWidth
                                                      size="small"
                                                      value={stage._id || stage.id}
                                                      InputProps={{ 
                                                        readOnly: true,
                                                        className: "stage-id-input"
                                                      }}
                                                      className="stage-id-textfield"
                                                    />
                                                    <IconButton
                                                      size="small"
                                                      className="stage-id-copy-btn"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const stageId = stage._id || stage.id;
                                                        if (stageId) {
                                                          navigator.clipboard.writeText(stageId).then(() => {
                                                            EventBus.publish('success', 'Internal id copied to clipboard');
                                                            setAnchorEl(null);
                                                            setPopoverStageId(null);
                                                          }).catch(() => {
                                                            EventBus.publish('error', 'Failed to copy to clipboard');
                                                          });
                                                        }
                                                      }}
                                                    >
                                                      <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                  </Box>
                                                </Paper>
                                              </Popover>
                                            </>
                                          )}
                                        </div>
                                      </Box>
                                    </div>
                                  </TableCell>

                                  <TableCell>
                                    {(() => {
                                      // Only show deal count when editing existing pipeline
                                      if (isCreateMode) return null;

                                      const pipelineStage = pipelineBoard['stages']?.find(s => s['_id'] === stage.id || s['id'] === stage.id || s['stageId'] === stage.id);
                                      const dealCount = pipelineStage?.['deals']?.length || 0;
                                      return (
                                        <Typography variant="body2" color="textSecondary">
                                          {dealCount} {dealCount === 1 ? 'deal' : 'deals'}
                                        </Typography>
                                      );
                                    })()}
                                  </TableCell>

                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      type="number"
                                      // label="Probability %"
                                      value={stage.probability}
                                      onWheel={(e) => e.target.blur()}
                                      onChange={(e) =>
                                        handleStageChange(
                                          stage.id,
                                          "probability",
                                          parseInt(e.target.value) || 0
                                        )
                                      }
                                      className="probability-input"
                                      // InputProps={{
                                      //   inputProps: { min: 0, max: 100 },
                                      //   endAdornment: (
                                      //     <InputAdornment position="end">%</InputAdornment>
                                      //   )
                                      // }}
                                    />
                                  </TableCell>

                                  <TableCell>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={stage.isWon}
                                          onChange={(e) => {
                                            handleStageChange(stage.id, "isWon", e.target.checked);
                                            if (e.target.checked) {
                                              handleStageChange(stage.id, "isLost", false);
                                              handleStageChange(stage.id, "probability", 100);
                                            }
                                          }}
                                        />
                                      }
                                      // label="Won"
                                    />
                                  </TableCell>

                                  <TableCell>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={stage.isLost}
                                          onChange={(e) => {
                                            handleStageChange(stage.id, "isLost", e.target.checked);
                                            if (e.target.checked) {
                                              handleStageChange(stage.id, "isWon", false);
                                              handleStageChange(stage.id, "probability", 0);
                                            }
                                          }}
                                        />
                                      }
                                      // label="Lost"
                                    />
                                  </TableCell>

                                  <TableCell align="right">
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      onClick={() => handleRemoveStage(stage.id)}
                                      color={(() => {
                                        // New pipelines have no deals, so always use default color
                                        if (isCreateMode) return 'default';
                                        
                                        const pipelineStage = pipelineBoard['stages']?.find(s => s['_id'] === stage.id || s['id'] === stage.id || s['stageId'] === stage.id);
                                        return pipelineStage?.['deals']?.length > 0 ? 'error' : 'default';
                                      })()}
                                      title={(() => {
                                        // New pipelines have no deals, so use simple tooltip
                                        if (isCreateMode) return 'Delete stage';
                                        
                                        const pipelineStage = pipelineBoard['stages']?.find(s => s['_id'] === stage.id || s['id'] === stage.id || s['stageId'] === stage.id);
                                        const dealCount = pipelineStage?.['deals']?.length || 0;
                                        return dealCount > 0 ? `Cannot delete - contains ${dealCount} deal${dealCount > 1 ? 's' : ''}` : 'Delete stage';
                                      })()}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                                );
                              }}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </TableBody>
                      </Table>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
              <div className='group-form add-stage'>
                <TextField
                  size="small"
                  placeholder="New stage name"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e['target']['value'])}
                  onKeyPress={(e) => e['key'] === 'Enter' && handleAddStage()}
                  className="new-stage-input"
                />
                <button className='btn-style-border' onClick={handleAddStage}><AddIcon /> Add Stage</button>
              </div>
            </div>
            <div className='group-form btn-groups'>
              {!isCreateMode && (
                <button onClick={() => handleDeletePipeline()} className='btn-style-border delete-btn'>
                  Delete Pipeline
                </button>
              )}
              <button onClick={handleClose} className='btn-style-border'>Cancel</button>
              <button onClick={handleSubmit} className='btn-style-new'>
                {isCreateMode ? 'Create Pipeline' : 'Save Changes'}
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Delete Pipeline Confirmation Modal */}
      <Modal isOpen={isDeletePipelineModalOpen} toggle={() => setIsDeletePipelineModalOpen(false)} keyboard={true} className={`main-modal modal-dialog-centered new-user-modal`}>
        <ModalHeader toggle={() => setIsDeletePipelineModalOpen(false)}></ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='delete-modal'>
              <h4>
                <i className='icon'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='#FF3B30' fillOpacity='0.15' />
                    <path d='M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z' fill='#FF3B30' />
                    <path d='M12 9V12' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    <path d='M12 15H12.0083' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                </i>
                Delete Pipeline
              </h4>
              <p>
                Are you sure you want to delete <b>{formData['name']}</b> pipeline? You can't undo this action afterwards.
              </p>
              <div className='form-area'>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={() => setIsDeletePipelineModalOpen(false)}>Cancel</button>
                  <button className='btn-style-new' onClick={() => handleDeletePipeline(true)}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PipelineSettings;